import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Badge, 
  Menu, 
  MenuItem, 
  Tooltip,
  CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { fetchActiveAlerts, Alert as AlertType } from '../services/ApiService';

interface HeaderProps {}

const AppHeader: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Charger les alertes actives depuis l'API
  useEffect(() => {
    const loadActiveAlerts = async () => {
      setLoading(true);
      try {
        const data = await fetchActiveAlerts();
        setAlerts(data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des alertes:', err);
        setError('Impossible de charger les alertes');
      } finally {
        setLoading(false);
      }
    };

    loadActiveAlerts();
    
    // Rafraîchir les alertes toutes les 60 secondes
    const interval = setInterval(loadActiveAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAlertClick = () => {
    navigate('/alerts');
    handleClose();
  };

  const handleSettingsClick = () => {
    navigate('/alerts');  // Rediriger vers les paramètres d'alertes
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          SONDE-THERMO | Supervision V3.4.0
        </Typography>
        
        <Tooltip title="Alertes">
          <IconButton 
            color="inherit" 
            onClick={handleNotificationClick}
            aria-label="alertes"
          >
            <Badge badgeContent={alerts.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        
        <Menu
          id="notifications-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'notifications-button',
          }}
        >
          {loading ? (
            <MenuItem>
              <CircularProgress size={20} sx={{ mr: 1 }} /> Chargement des alertes...
            </MenuItem>
          ) : error ? (
            <MenuItem onClick={handleClose}>{error}</MenuItem>
          ) : alerts.length > 0 ? (
            <>
              {alerts.slice(0, 3).map((alert) => (
                <MenuItem key={typeof alert.id === 'string' ? alert.id : String(alert.id)} onClick={handleAlertClick}>
                  {alert.constante} {alert.etat === 'active' ? 'Alerte' : 'Résolue'} - Boîtier #{alert.boitier_id}
                </MenuItem>
              ))}
              {alerts.length > 3 && (
                <MenuItem onClick={handleAlertClick}>
                  Voir toutes les alertes ({alerts.length})...
                </MenuItem>
              )}
            </>
          ) : (
            <MenuItem onClick={handleClose}>Aucune alerte active</MenuItem>
          )}
        </Menu>
        
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
